import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface VolumeControlProps {
  showVolumeControl: boolean;
  volume: number;
  setVolume: (volume: number) => void;
  setIsMuted: (muted: boolean) => void;
  audioRef: React.RefObject<HTMLAudioElement>;
  volumeHideTimeoutRef: React.RefObject<NodeJS.Timeout | null>;
  setShowVolumeControl: (show: boolean) => void;
}

const VolumeControl: React.FC<VolumeControlProps> = ({
  showVolumeControl,
  volume,
  setVolume,
  setIsMuted,
  audioRef,
  volumeHideTimeoutRef,
  setShowVolumeControl
}) => {
  // Add state to track when slider is being dragged
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  
  // Handle drag events
  useEffect(() => {
    if (!isDragging || !sliderRef.current) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!sliderRef.current) return;
      
      // Prevent default to avoid text selection during drag
      e.preventDefault();
      
      // Calculate position relative to container
      const rect = sliderRef.current.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const containerHeight = rect.height;
      
      // Convert to volume (0-1) - inverted because 0 is at the bottom, 1 at the top
      const newVolume = 1 - Math.max(0, Math.min(1, y / containerHeight));
      
      // Update volume
      setVolume(newVolume);
      if (audioRef.current) {
        audioRef.current.volume = newVolume;
      }
      setIsMuted(newVolume === 0);
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
    };
    
    // Add event listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    // Clean up
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, setVolume, setIsMuted, audioRef]);

  return (
    <AnimatePresence>
      {showVolumeControl && (
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 10 }}
          transition={{ duration: 0.2 }}
          className="absolute bottom-full mb-2 bg-black p-2 shadow-lg"
          onMouseEnter={() => {
            // Clear timeout when mouse enters the volume slider
            if (volumeHideTimeoutRef.current) {
              clearTimeout(volumeHideTimeoutRef.current);
            }
          }}
          onMouseLeave={() => {
            // Hide after delay when mouse leaves the slider
            const timeoutId = setTimeout(() => {
              setShowVolumeControl(false);
            }, 500); // shorter delay when leaving the slider
            
            // Set the timeout ref in a safe way
            if (volumeHideTimeoutRef.current === null) {
              // Use a function that properly manages the ref
              const setter = volumeHideTimeoutRef as unknown as { current: NodeJS.Timeout | null };
              setter.current = timeoutId;
            }
          }}
        >
          <div 
            ref={sliderRef}
            className="h-20 w-3 bg-white relative cursor-pointer progress-bar volume-slider-track"
            onClick={(e) => {
              // Handle direct click to set volume immediately
              e.stopPropagation();
              e.preventDefault();
              
              // Calculate position relative to container height
              const rect = e.currentTarget.getBoundingClientRect();
              const clickY = e.clientY - rect.top;
              const containerHeight = rect.height;
              
              // Convert to volume (0-1) - inverted because 0 is at the bottom, 1 at the top
              const newVolume = 1 - (clickY / containerHeight);
              // Clamp value between 0 and 1
              const clampedVolume = Math.max(0, Math.min(1, newVolume));
              
              // Update volume
              setVolume(clampedVolume);
              if (audioRef.current) {
                audioRef.current.volume = clampedVolume;
              }
              setIsMuted(clampedVolume === 0);
            }}
            onMouseDown={(e) => {
              e.stopPropagation(); // Prevent event from bubbling up
              e.preventDefault(); // Prevent default to avoid text selection
              setIsDragging(true);
            }}
          >
            {/* Slider knob */}
            <div 
              className="absolute w-3 h-2.5" 
              style={{ 
                background: 'red',
                top: `${(1 - volume) * 100}%`,
                left: 0,
                right: 0
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsDragging(true);
              }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VolumeControl; 